import { Close as CloseIcon, ExpandMore as ExpandMoreIcon, Replay as ReplayIcon } from 'components/icons';
import { FC, Fragment, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { buttonStyle, GLYPH_OWN_SIZE, ICON_SLOT } from 'components/buttons/buttonStyle';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Button } from 'components/ui/button';
import { Drawer, DrawerContent } from 'components/ui/drawer';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { useJoinedPanel } from 'layout/NewDesignLayout/joinedPanels';
import { DETACHED_DRAWER_CLASS } from 'layout/NewDesignLayout/panelBox';
import {
    ProcessExecutionItemDto,
    ProcessType,
    useGetProcessExecutionItemsQuery,
    useRetryProcessExecutionItemMutation,
} from 'store/api/processes';
import { cn } from 'utils/cn';
import { useHasPermissions } from 'utils/permissionUtil';
import { ProcessChip } from './ProcessChip';
import { ProcessesTablePagination } from './ProcessesTablePagination';

/** The items table's cells, at the compact density: 8px of padding, the row's ends still 16px in. */
const HEAD_CELL_CLASS = cn(SIMPLE_TABLE_HEAD_CLASS, 'p-2');
const BODY_CELL_CLASS = cn(SIMPLE_TABLE_CELL_CLASS, 'p-2');

const PAGE_SIZE = 20;

const STATUS_CHIP_PROPS: Record<string, { labelId: string; color: 'success' | 'warning' | 'error' }> = {
    PROCESSED: { labelId: 'processes.items.status-processed', color: 'success' },
    SKIPPED: { labelId: 'processes.items.status-skipped', color: 'warning' },
    FAILED: { labelId: 'processes.items.status-failed', color: 'error' },
};

interface Props {
    executionId: number | null;
    processType: ProcessType | null;
    onClose: () => void;
}

const RETRYABLE_PROCESS_TYPES = new Set<ProcessType>([
    'NETVISOR_DIMENSION_SYNC',
    'NETVISOR_PARTY_SYNC',
    'NETVISOR_EXPENSE_POLL',
    'NETVISOR_PAYMENT_POLL',
]);

const NON_RETRYABLE_PAYMENT_ITEM_PREFIX = 'invoice-status-recalc:';
const NON_RETRYABLE_PAYMENT_ITEM_ERROR_CODE = 'INTERNAL_ERROR';

interface DrawerContentProps {
    executionId: number;
    processType: ProcessType | null;
    onClose: () => void;
}

const ProcessExecutionItemDrawerContent: FC<DrawerContentProps> = ({ executionId, processType, onClose }) => {
    const intl = useIntl();
    const [page, setPage] = useState(0);
    const [statusFilter, setStatusFilter] = useState<string | undefined>();
    const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
    const canSeeTechnicalDetails = useHasPermissions(['READ.PROCESS_TECHNICAL_DETAILS']);
    const canRunProcesses = useHasPermissions(['RUN.PROCESSES']);
    const isRetrySupported = processType !== null && RETRYABLE_PROCESS_TYPES.has(processType);

    const { data, isLoading } = useGetProcessExecutionItemsQuery(
        { executionId, status: statusFilter, page, size: PAGE_SIZE },
        { skip: false }
    );

    const [retryItem, { isLoading: isRetrying }] = useRetryProcessExecutionItemMutation();
    const isAnyRetrying = isRetrying;

    const handleRetry = (itemId: number) => {
        if (!canRunProcesses || isAnyRetrying) return;
        retryItem({ executionId, itemId });
    };

    const isPaymentStatusRecalculationFailure = (item: ProcessExecutionItemDto) =>
        item.itemType === 'PAYMENT' &&
        item.internalId !== null &&
        item.errorCode === NON_RETRYABLE_PAYMENT_ITEM_ERROR_CODE &&
        item.externalKey.startsWith(NON_RETRYABLE_PAYMENT_ITEM_PREFIX);

    const canRetryItem = (item: ProcessExecutionItemDto) => {
        if (!canRunProcesses || !isRetrySupported || isAnyRetrying) return false;
        if (item.status !== 'FAILED' && item.status !== 'SKIPPED') return false;
        if (processType === 'NETVISOR_PAYMENT_POLL') {
            return item.itemType === 'PAYMENT' && !isPaymentStatusRecalculationFailure(item);
        }
        if (processType === 'NETVISOR_EXPENSE_POLL') {
            return item.itemType === 'PURCHASE_INVOICE' || item.itemType === 'INVOICE';
        }
        if (processType === 'NETVISOR_PARTY_SYNC') {
            return item.itemType === 'PARTY' && item.internalId !== null;
        }
        if (processType === 'NETVISOR_DIMENSION_SYNC' && item.internalId === null) return false;
        return true;
    };

    const formatRetryInfo = (item: ProcessExecutionItemDto) => {
        if (item.retryCount === 0) return null;
        const lastRetry = item.lastRetryAt
            ? intl.formatDate(item.lastRetryAt, { hour: '2-digit', minute: '2-digit', hour12: false })
            : '';
        if (lastRetry) {
            return intl.formatMessage(
                { id: 'processes.items.retried-count-last' },
                { count: item.retryCount, lastRetry }
            );
        }
        return intl.formatMessage({ id: 'processes.items.retried-count' }, { count: item.retryCount });
    };

    const items = data?.content ?? [];
    const hasItems = items.length > 0;
    const emptyStateMessage = statusFilter
        ? intl.formatMessage({
              id: 'processes.items.empty-filtered',
              defaultMessage: 'No items match this filter.',
          })
        : intl.formatMessage({
              id: 'processes.items.empty',
              defaultMessage: 'No items for this execution.',
          });

    return (
        <div className="overflow-y-auto p-6">
            <div className="mb-4 flex items-center justify-between">
                <h2 className="text-h6">
                    <FormattedMessage id="processes.items.title" />
                </h2>
                <Button
                    variant="ghost"
                    size="icon-lg"
                    aria-label={intl.formatMessage({ id: 'close' })}
                    className={cn(GLYPH_OWN_SIZE, 'rounded-full')}
                    onClick={onClose}
                >
                    <CloseIcon />
                </Button>
            </div>

            <div className="mb-4 flex gap-2">
                {[undefined, 'PROCESSED', 'SKIPPED', 'FAILED'].map((filter) => (
                    <ProcessChip
                        key={filter ?? 'all'}
                        outlined={statusFilter !== filter}
                        className={cn(
                            'cursor-pointer',
                            statusFilter === filter ? 'hover:bg-grey-300' : 'hover:bg-grey-100'
                        )}
                        render={<button type="button" aria-pressed={statusFilter === filter} />}
                        onClick={() => {
                            setStatusFilter(filter);
                            setPage(0);
                            setExpandedItemId(null);
                        }}
                    >
                        {filter
                            ? intl.formatMessage({
                                  id: STATUS_CHIP_PROPS[filter]?.labelId ?? filter,
                              })
                            : intl.formatMessage({ id: 'processes.items.filter-all' })}
                    </ProcessChip>
                ))}
            </div>

            {isLoading ? (
                <p className="text-body1">
                    <FormattedMessage id="processes.items.loading" />
                </p>
            ) : !hasItems ? (
                <p className="text-body1 text-fg-secondary">{emptyStateMessage}</p>
            ) : (
                <>
                    <div className="relative w-full overflow-auto">
                        <table className="w-full text-left text-sm">
                            <TableHeader className="[&_tr]:border-b-0">
                                <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                    <TableHead className={HEAD_CELL_CLASS}>
                                        <FormattedMessage id="processes.items.column-key" />
                                    </TableHead>
                                    <TableHead className={HEAD_CELL_CLASS}>
                                        <FormattedMessage id="processes.items.column-status" />
                                    </TableHead>
                                    <TableHead className={HEAD_CELL_CLASS}>
                                        <FormattedMessage id="processes.items.column-message" />
                                    </TableHead>
                                    <TableHead className={HEAD_CELL_CLASS} />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((item) => {
                                    const chipProps = STATUS_CHIP_PROPS[item.status];
                                    const retryInfo = formatRetryInfo(item);
                                    const isExpanded = expandedItemId === item.id;
                                    const hasDetailRow = canSeeTechnicalDetails && isExpanded && item.errorDetail;

                                    return (
                                        <Fragment key={item.id}>
                                            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                                <TableCell className={BODY_CELL_CLASS}>
                                                    <p
                                                        className="text-body2 max-w-40 truncate"
                                                        title={item.externalKey}
                                                    >
                                                        {item.externalKey}
                                                    </p>
                                                </TableCell>
                                                <TableCell className={BODY_CELL_CLASS}>
                                                    <ProcessChip color={chipProps?.color ?? 'default'}>
                                                        {chipProps
                                                            ? intl.formatMessage({ id: chipProps.labelId })
                                                            : item.status}
                                                    </ProcessChip>
                                                </TableCell>
                                                <TableCell className={BODY_CELL_CLASS}>
                                                    {item.errorMessage && (
                                                        <p className="text-body2">{item.errorMessage}</p>
                                                    )}
                                                    {retryInfo && (
                                                        <span className="text-caption text-fg-secondary">
                                                            {retryInfo}
                                                        </span>
                                                    )}
                                                    {canSeeTechnicalDetails && item.errorDetail && (
                                                        <Button
                                                            {...buttonStyle({ variant: 'text' }, 'mt-1 flex px-1')}
                                                            onClick={() =>
                                                                setExpandedItemId(isExpanded ? null : item.id)
                                                            }
                                                        >
                                                            <span className={ICON_SLOT.small}>
                                                                <ExpandMoreIcon
                                                                    className={cn(
                                                                        'new:motion-safe:transition-transform new:duration-(--duration-fast) new:ease-(--ease-smooth-out)',
                                                                        isExpanded && '-scale-y-100'
                                                                    )}
                                                                />
                                                            </span>
                                                            <FormattedMessage id="processes.items.technical-details" />
                                                        </Button>
                                                    )}
                                                </TableCell>
                                                <TableCell className={BODY_CELL_CLASS}>
                                                    {canRetryItem(item) && (
                                                        <Button
                                                            {...buttonStyle({ variant: 'outlined' })}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleRetry(item.id);
                                                            }}
                                                            disabled={isAnyRetrying}
                                                        >
                                                            <span className={ICON_SLOT.small}>
                                                                <ReplayIcon />
                                                            </span>
                                                            <FormattedMessage id="processes.items.retry" />
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            {hasDetailRow && (
                                                <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                                    <TableCell colSpan={4} className={BODY_CELL_CLASS}>
                                                        <div className="bg-grey-100 rounded-sm p-2 font-mono text-xs break-all whitespace-pre-wrap">
                                                            {item.errorDetail}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </TableBody>
                        </table>
                    </div>

                    {data && data.totalElements > 0 && (
                        <ProcessesTablePagination
                            count={data.totalElements}
                            page={page}
                            onPageChange={setPage}
                            rowsPerPage={PAGE_SIZE}
                        />
                    )}
                </>
            )}
        </div>
    );
};

const ProcessExecutionItemDrawer: FC<Props> = ({ executionId, processType, onClose }) => {
    const intl = useIntl();
    const isNewDesign = useIsNewDesign();
    // In the new design it is one of the side panels of which one is open at a time.
    useJoinedPanel(executionId !== null, onClose);
    return (
        <Drawer open={executionId !== null} onOpenChange={(open) => !open && onClose()} swipeDirection="right">
            <DrawerContent
                aria-label={intl.formatMessage({ id: 'processes.items.title' })}
                // The kit's side drawer is 24rem wide (75% below `sm`) with a rounded, bordered inner
                // edge; this one is a fixed 700px sheet flush with the viewport edge, and in the new
                // design a detached box of that width.
                className={
                    isNewDesign
                        ? cn(DETACHED_DRAWER_CLASS, 'w-[700px]')
                        : 'bg-paper shadow-peek w-[700px] max-w-full data-[swipe-direction=right]:rounded-none data-[swipe-direction=right]:border-0'
                }
            >
                {executionId !== null && (
                    <ProcessExecutionItemDrawerContent
                        key={executionId}
                        executionId={executionId}
                        processType={processType}
                        onClose={onClose}
                    />
                )}
            </DrawerContent>
        </Drawer>
    );
};

export default ProcessExecutionItemDrawer;
